import { useMemo, useState, useCallback } from 'react';
import type { Metro } from '../types/metro';
import { useFilteredMetros } from './useFilteredMetros';
import { useRankingMetrics, type RankingMetric } from './useRankingMetrics';
import { useFilterStore } from '../stores/filterStore';
import { lookupSectorByChipId } from '../lib/occupation-utils';

/** Columns the rankings table can sort by. */
export type RankingSortKey =
  | 'ratio'
  | 'name'
  | 'totalCredentials'
  | 'totalJobs'
  | 'gap'
  // Q2.A columns (RANK-fix-4):
  | 'springboardCount'
  | 'shortageShare'
  // 2026-05-18 feedback: when an occupational group is selected, the
  // table swaps to lens-specific columns (Number of credentials,
  // Number of jobs, Credential shortage). These sort keys read from
  // `metric.lensCredentials` / `metric.lensJobs` / `metric.lensRatio`.
  | 'lensCredentials'
  | 'lensJobs';

/** Sort direction — `null` means "no sort yet" (used by SortableHeader for aria). */
export type SortDirection = 'asc' | 'desc';

export interface RankingRow {
  /** 1-based rank within the currently filtered+sorted view. */
  readonly rank: number;
  readonly metro: Metro;
  /** Derived: jobs - credentials. Negative for surplus, positive for shortage. */
  readonly gap: number;
  /** Per-metro derived metrics (Q2.A columns). */
  readonly metric: RankingMetric;
}

interface UseRankingsResult {
  readonly rows: ReadonlyArray<RankingRow>;
  readonly sortKey: RankingSortKey;
  readonly sortDir: SortDirection;
  /**
   * Click handler for column headers. Same column → flip direction.
   * Different column → switch key + reset direction to ascending.
   */
  readonly toggleSort: (key: RankingSortKey) => void;
  /**
   * The lens sector display name (e.g. `'STEM'`) when an
   * occupational group is selected. `null` when no lens is active.
   * Exposed so the table view can render its "Showing X data"
   * banner without re-translating the filter store id.
   */
  readonly lensSectorName: string | null;
}

/**
 * Filter (via the cross-page filterStore), sort by a chosen column, and
 * assign 1-based ranks. The filter is composed from useFilteredMetros so
 * Map and Rankings stay in lockstep — toggling a status pill on Map
 * narrows what shows up here, and vice versa.
 *
 * RANK-fix-4: integrated `useRankingMetrics()` so each row carries its
 * computed Springboard Occs / Shortage Share / Most Underproduced.
 * The two new sort keys (`springboardCount`, `shortageShare`) read
 * from those metrics; legacy keys (ratio, name, gap, etc.) still work
 * so existing callers don't break.
 *
 * RANK-fix-7 (code-review apply): `toggleSort` no longer calls
 * `setSortDir` from inside `setSortKey`'s updater. Calling a setter
 * inside another setter's updater is a side effect that React's
 * StrictMode double-invokes (it runs each updater twice to surface
 * impurity), which silently caused the direction to flip twice on the
 * first click of a new column. The new implementation reads the
 * current key once and dispatches both setters at the top level.
 *
 * RANKINGS-MARIA-fix-1 (2026-05-07): when an occupational group is
 * picked in the filter store, the metrics hook is now lensed through
 * that sector's name. Sort by the `'ratio'` key uses the
 * sector-specific ratio when the lens is active, so the resulting
 * order reflects the user's chosen group instead of the metro-wide
 * average. The lens sector name is also returned so the table view
 * can render an explanatory banner.
 *
 * RANKINGS-POLISH (2026-05-07, code-reviewer suggestion #1): the
 * inline `OCC_CHIP_TO_SECTOR as Record<string, string | undefined>`
 * cast moved into `lookupSectorByChipId()` in `lib/occupation-utils.ts`.
 * The cast (and its `null` fallback) now lives in exactly one place.
 */
export function useRankings(metros: ReadonlyArray<Metro>): UseRankingsResult {
  const filtered = useFilteredMetros(metros);
  const occupationalGroupId = useFilterStore((s) => s.occupationalGroupId);
  const lensSectorName = useMemo<string | null>(
    () => lookupSectorByChipId(occupationalGroupId),
    [occupationalGroupId],
  );
  const metrics = useRankingMetrics(filtered, lensSectorName);
  const [sortKey, setSortKey] = useState<RankingSortKey>('ratio');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const toggleSort = useCallback((key: RankingSortKey) => {
    // Pure: dispatch the next key directly (no need for an updater
    // function since we don't read the previous value).
    setSortKey(key);
    // Direction depends on whether this is the same column as the
    // active sort. Read `sortKey` via the closure — safe because
    // React batches both setters and the next render sees both
    // updates atomically.
    setSortDir((currentDir) =>
      sortKey === key ? (currentDir === 'asc' ? 'desc' : 'asc') : 'asc',
    );
  }, [sortKey]);

  const rows = useMemo<ReadonlyArray<RankingRow>>(() => {
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'totalCredentials':
          cmp = a.totalCredentials - b.totalCredentials;
          break;
        case 'totalJobs':
          cmp = a.totalJobs - b.totalJobs;
          break;
        case 'gap':
          cmp = (a.totalJobs - a.totalCredentials) - (b.totalJobs - b.totalCredentials);
          break;
        case 'springboardCount': {
          const ma = metrics.get(a.id)?.springboardCount ?? 0;
          const mb = metrics.get(b.id)?.springboardCount ?? 0;
          cmp = ma - mb;
          break;
        }
        case 'shortageShare': {
          const ma = metrics.get(a.id)?.shortageShare ?? 0;
          const mb = metrics.get(b.id)?.shortageShare ?? 0;
          cmp = ma - mb;
          break;
        }
        case 'lensCredentials': {
          const ma = metrics.get(a.id)?.lensCredentials ?? 0;
          const mb = metrics.get(b.id)?.lensCredentials ?? 0;
          cmp = ma - mb;
          break;
        }
        case 'lensJobs': {
          const ma = metrics.get(a.id)?.lensJobs ?? 0;
          const mb = metrics.get(b.id)?.lensJobs ?? 0;
          cmp = ma - mb;
          break;
        }
        case 'ratio':
        default: {
          // RANKINGS-MARIA-fix-1: when lensed, sort by the sector's
          // ratio so the column order matches the column values.
          // Without this, sorting by Severity would order by the
          // metro-wide ratio while the displayed numbers reflect the
          // sector ratio — visually broken.
          const ra = metrics.get(a.id)?.lensRatio ?? a.ratio;
          const rb = metrics.get(b.id)?.lensRatio ?? b.ratio;
          cmp = ra - rb;
        }
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted.map((metro, i) => ({
      rank: i + 1,
      metro,
      gap: metro.totalJobs - metro.totalCredentials,
      metric: metrics.get(metro.id) ?? {
        springboardCount: 0,
        shortageShare: 0,
        mostUnderproducedGroup: '—',
      },
    }));
  }, [filtered, metrics, sortKey, sortDir]);

  return { rows, sortKey, sortDir, toggleSort, lensSectorName };
}
